import React from 'react';

/**
 * EmptyState — single shared "nothing here yet" placeholder.
 *
 * Replaces six divergent inline empty-state renderings across the app.
 * Uses design tokens (--fs-*, --r-*, --mute) so it matches every surface.
 *
 * Props:
 *   icon       — optional lucide-react icon component (rendered at 32px)
 *   title      — one-line heading, e.g. "No clients yet"
 *   detail     — optional secondary sentence
 *   cta        — optional <button> or <Link> for a primary action
 *   dataTestId — override the default 'empty-state' testid
 *   compact    — bool; reduces vertical padding for inline empties
 */
export const EmptyState = ({
  icon: Icon,
  title,
  detail,
  cta,
  dataTestId = 'empty-state',
  compact = false,
}) => (
  <div
    className="empty-state"
    data-testid={dataTestId}
    style={{
      padding: compact ? '20px 16px' : '48px 20px',
      textAlign: 'center',
      color: 'var(--mute)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
    }}
  >
    {Icon && (
      <Icon
        size={compact ? 24 : 32}
        strokeWidth={1.5}
        style={{ color: 'var(--mute2, var(--mute))', opacity: 0.7 }}
      />
    )}
    <div style={{
      fontSize: compact ? 'var(--fs-base)' : 'var(--fs-lg)',
      fontWeight: 600,
      color: 'var(--ink)',
    }}>
      {title}
    </div>
    {detail && (
      <div style={{
        fontSize: 'var(--fs-sm)',
        maxWidth: 360,
        lineHeight: 1.5,
      }}>
        {detail}
      </div>
    )}
    {cta && <div style={{ marginTop: 6 }}>{cta}</div>}
  </div>
);

export default EmptyState;
